import { deviceApi } from "#/api/deviceApi";
import { type Device } from "#/types/device";
import { logger } from "#/utils/logger";
import { Button, Modal, type ModalProps } from "@mantine/core";
import { IconDeviceMobileCheck, IconDeviceMobileOff } from "@tabler/icons-react";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

export interface Props extends ModalProps {
  /** The scanner whose active flag is being flipped. The modal switches direction based on its current flag. */
  device: Device | undefined;
  onSuccess: () => void;
}

/**
 * Confirmation before switching a scanner on or off. Off is consequential:
 * the backend rejects the device on every call, the app logs out, and a C72
 * drops its queued offline scans — so both directions ask first.
 */
const DeviceStatusModal = ({ device, onSuccess, ...props }: Props) => {
  const { t } = useTranslation();
  const isActive = device?.is_active === true;
  const target = !isActive;

  const copy = isActive
    ? {
        title: t("devices.status.deactivateTitle"),
        description: t("devices.status.deactivateDescription"),
        warning: t("devices.status.deactivateWarning"),
        confirmation: t("devices.status.deactivateConfirmation"),
        button: t("devices.status.deactivateButton"),
        color: "red",
        Icon: IconDeviceMobileOff,
      }
    : {
        title: t("devices.status.activateTitle"),
        description: t("devices.status.activateDescription"),
        warning: t("devices.status.activateWarning"),
        confirmation: t("devices.status.activateConfirmation"),
        button: t("devices.status.activateButton"),
        color: "teal",
        Icon: IconDeviceMobileCheck,
      };

  const updateStatus = useMutation({
    mutationFn: async () => {
      if (!device) throw new Error("No device selected");
      return await deviceApi.update(device.id, { is_active: target });
    },
    onSuccess: () => {
      onSuccess();
    },
    onError: (error) => {
      logger.error("DeviceStatusModal updateStatus error:", error);
    },
  });

  return (
    <Modal
      {...props}
      centered
      title={copy.title}
      closeOnClickOutside={false}
      onClose={() => {
        props.onClose();
      }}
      classNames={{
        title: "font-bold",
      }}
    >
      <div className="w-full flex flex-col items-center justify-center gap-4">
        <div className="flex flex-col gap-2 text-sm">
          <span>{copy.description}</span>
          <div className="flex flex-col gap-1 mx-auto my-4 bg-slate-100 border border-dashed border-slate-400 rounded-lg p-4 w-full text-center">
            <span className="font-bold">{device?.name ?? "--"}</span>
            <span className="break-all">{device?.serial_number ?? "--"}</span>
          </div>
          <span>{copy.warning}</span>

          <span className="font-bold my-2">{copy.confirmation}</span>
        </div>

        <div className="w-full grid grid-cols-[auto_1fr] items-center gap-2">
          <Button size="sm" color="black" onClick={() => props.onClose()}>
            {t("common.abort")}
          </Button>
          <Button
            onClick={() => updateStatus.mutate()}
            color={copy.color}
            size="sm"
            loading={updateStatus.isPending}
            disabled={!device}
            leftSection={<copy.Icon size={18} />}
          >
            {copy.button}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default DeviceStatusModal;
